import { useEffect } from "react";
import { useTranslation } from "react-i18next";

export type Bank = "dsk" | "postbank";

interface Props {
  /** null while availability is still loading. */
  available: Record<Bank, boolean> | null;
  /** A bank is being contacted — the button spins until the redirect. */
  busy: boolean;
  onSelect: () => void;
  /** Replaces the description of a bank that cannot be used. */
  unavailableText?: string;
  /** Called when Back restores the page from the bfcache mid-redirect. */
  onRestore: () => void;
}

export default function BankOptions({
  available,
  busy,
  onSelect,
  unavailableText,
  onRestore,
}: Props) {
  const { t } = useTranslation();

  // Coming back from a bank page restores this page from the bfcache with its
  // buttons still spinning.
  useEffect(() => {
    const reset = (e: PageTransitionEvent) => {
      if (e.persisted) onRestore();
    };
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, [onRestore]);

  // Usable while either bank is: the server only draws from live ones.
  const unavailable = available !== null && !available.dsk && !available.postbank;

  // One card option; the server picks DSK Bank or PostBank for it.
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-background-200/70 bg-background-50 p-6 flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="w-12 h-12 shrink-0 rounded-xl bg-primary-50 flex items-center justify-center">
            <i className="ri-bank-card-line text-2xl text-primary-600"></i>
          </div>
          <div className="min-w-0">
            <p className="font-heading font-bold text-foreground-950">{t("pay.card.name")}</p>
            <p className="mt-0.5 text-sm text-foreground-600">
              {unavailable
                ? (unavailableText ?? t("pay.card.unavailable"))
                : t("pay.card.description")}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onSelect}
          disabled={unavailable || busy}
          className="whitespace-nowrap rounded-full bg-gradient-to-r from-primary-500 to-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-transform hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 cursor-pointer"
        >
          {busy ? (
            <>
              <i className="ri-loader-4-line animate-spin mr-1.5"></i>
              {t("pay.redirecting")}
            </>
          ) : (
            t("pay.card.proceed")
          )}
        </button>
      </div>
    </div>
  );
}
